import {
  Building2,
  ChevronDown,
  ChevronRight,
  Filter,
  Search,
} from "lucide-react";
import {
  Fragment,
} from "react";
import {
  can,
} from "../../../../shared/auth/access";
import { TableState } from "../../../../shared/components/TableState";
import {
  asProcurementRole,
} from "../../domain/models";
import {
  procurementWorkspaceTarget,
  specialistNextStep,
} from "../../domain/selectors";
import {
  formatDisplayDate,
  formatRemainingDays,
  procurementStageOf,
} from "../../model";
import { PlanningRequestInline } from "../planning/PlanningRequestInline";
import { PlanningRequestProgress } from "../planning/PlanningRequestProgress";
import { ProcurementQueueOpenIndicator } from "../shared/ProcurementQueueOpenIndicator";
import type {
  useRequestsWorkspace,
} from "./useRequestsWorkspace";

export function RequestQueue({ model }: { model: ReturnType<typeof useRequestsWorkspace>; }) {
  const {
    requestWorkspaceService,
    user,
    query,
    setQuery,
    statusFilter,
    setStatusFilter,
    departmentFilter,
    setDepartmentFilter,
    loading,
    loadError,
    setReloadKey,
    expandedRequest,
    stageOptions,
    selectedStageOption,
    departments,
    showDepartmentFilter,
    visibleRows,
    openRequest,
    openRequestDetail,
  } = model;
  return (<>
    <section
      className={`module-card${user.role === "SATIN_ALMA" || user.role === "PLANLAMA"
          ? " procurement-queue-surface"
          : ""
        }${user.role === "PLANLAMA" ? " planning-request-list" : ""}`}
    >
      <div className="module-toolbar request-queue-toolbar procurement-queue-toolbar">
        {user.role !== "SATIN_ALMA" && user.role !== "PLANLAMA" ? (
          <div className="request-queue-heading">
            <strong>{selectedStageOption?.label ?? "Talepler"}</strong>
            <small>{visibleRows.length} kayıt</small>
          </div>
        ) : null}
        <div className="module-search">
          <Search size={16} />
          <input
            placeholder="Talep numarası veya ürün ara"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        {user.role === "PLANLAMA" ? (
          <label className="toolbar-select planning-request-view-select">
            <Filter size={15} aria-hidden="true" />
            <select
              aria-label="Talep görünümü"
              value={statusFilter ?? "active"}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              {stageOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {showDepartmentFilter ? (
          <label className="toolbar-select">
            <Building2 size={15} />
            <select
              aria-label="Departman"
              value={departmentFilter}
              onChange={(event) => setDepartmentFilter(event.target.value)}
            >
              <option>Tümü</option>
              {departments.map((department) => (
                <option key={department}>{department}</option>
              ))}
            </select>
          </label>
        ) : null}
        {user.role === "SATIN_ALMA" || user.role === "PLANLAMA" ? (
          <span className="request-queue-count">
            {visibleRows.length}{" "}
            {user.role === "PLANLAMA" ? "kayıt" : "talep"}
          </span>
        ) : null}
      </div>
      {loading ? (
        <TableState
          title="Talepler yükleniyor"
          description="Satın alma kayıtları sunucudan alınıyor."
        />
      ) : loadError ? (
        <TableState
          title="Talepler yüklenemedi"
          description={loadError}
          action={
            <button
              className="secondary-button"
              onClick={() => setReloadKey((current) => current + 1)}
            >
              Yeniden dene
            </button>
          }
        />
      ) : visibleRows.length ? (
        <div className="data-table-wrap">
          <table
            className={`data-table request-table procurement-work-list${user.role === "PLANLAMA" ? " is-planning" : ""}${user.role === "SATIN_ALMA" ? " is-specialist" : ""}`}
          >
            <colgroup>
              <col className="request-table-main-column" />
              {user.role === "PLANLAMA" ? (
                <>
                  <col className="request-table-need-column" />
                  <col className="request-table-requester-column" />
                </>
              ) : null}
              {user.role === "SATIN_ALMA" ? (
                <>
                  <col className="request-table-quantity-column" />
                  <col className="request-table-owner-column" />
                </>
              ) : null}
              <col className="request-table-process-column" />
              {can(user, "offer.read") && user.role !== "SATIN_ALMA" ? (
                <col className="request-table-offer-column" />
              ) : null}
              {user.role !== "PLANLAMA" ? (
                <col className="request-table-due-column" />
              ) : null}
              <col className="request-table-action-column" />
            </colgroup>
            <thead
              className={
                user.role === "SATIN_ALMA"
                  ? "procurement-queue-header"
                  : undefined
              }
            >
              <tr>
                <th>Talep</th>
                {user.role === "PLANLAMA" ? (
                  <>
                    <th>İhtiyaç</th>
                    <th>Talep eden</th>
                  </>
                ) : null}
                {user.role === "SATIN_ALMA" ? (
                  <>
                    <th>Miktar</th>
                    <th>Talep eden</th>
                  </>
                ) : null}
                <th>
                  {user.role === "SATIN_ALMA" ? "Sıradaki iş" : "Süreç"}
                </th>
                {can(user, "offer.read") && user.role !== "SATIN_ALMA" ? (
                  <th>Teklifler</th>
                ) : null}
                {user.role !== "PLANLAMA" ? (
                  <th>
                    {user.role === "SATIN_ALMA" ? "Aciliyet" : "İhtiyaç"}
                  </th>
                ) : null}
                <th aria-label="Satır işlemi" />
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((request) => {
                const stage = procurementStageOf(request);
                const isCompleted = stage === "Tamamlandı";
                const opensInline =
                  procurementWorkspaceTarget(
                    asProcurementRole(user.role),
                    request,
                  ) === "inline";
                const nextStep =
                  user.role === "SATIN_ALMA"
                    ? specialistNextStep(request)
                    : null;
                const responsible =
                  stage === "Müdür Onayında"
                    ? "Satın Alma Müdürü"
                    : stage === "Genel Müdür Onayında"
                      ? "Genel Müdür"
                      : stage === "Tamamlandı"
                        ? "Süreç tamamlandı"
                        : "Satın Alma Uzmanı";
                const actionLabel = isCompleted
                  ? "Sonucu görüntüle"
                  : (nextStep?.action ??
                    (user.role === "SATIN_ALMA_MUDURU" ||
                      user.role === "GENEL_MUDUR"
                      ? "Kararı incele"
                      : user.role === "PLANLAMA"
                        ? "Süreci incele"
                        : "Talebi incele"));
                const returnContext = [...(request.approvalChain ?? [])]
                  .reverse()
                  .find(
                    (approval) =>
                      approval.status === "REVISION_REQUESTED" ||
                      approval.status === "REJECTED",
                  );
                const rowIsInteractive = true;
                const rowOpensWorkspace = user.role === "SATIN_ALMA";
                const rowOpensInline = user.role === "PLANLAMA";
                return (
                  <Fragment key={request.id}>
                    <tr
                      className={
                        `${rowIsInteractive ? "is-clickable" : ""}${user.role === "SATIN_ALMA" ? " procurement-queue-row" : ""}${expandedRequest === request.id ? " is-expanded" : ""}`.trim() ||
                        undefined
                      }
                      tabIndex={rowIsInteractive ? 0 : undefined}
                      role={rowIsInteractive ? "link" : undefined}
                      aria-label={
                        rowIsInteractive
                          ? isCompleted
                            ? `${request.title} talebinin sonucunu görüntüle`
                            : rowOpensWorkspace
                              ? `${request.title} teklif çalışma alanını aç`
                              : rowOpensInline
                                ? `${request.title} sürecini göster`
                                : `${request.title} talebinin ayrıntılarını görüntüle`
                          : undefined
                      }
                      onClick={
                        rowIsInteractive
                          ? () =>
                            rowOpensWorkspace || rowOpensInline
                              ? openRequest(request)
                              : openRequestDetail(request.id)
                          : undefined
                      }
                      onKeyDown={
                        rowIsInteractive
                          ? (event) => {
                            if (
                              event.key === "Enter" ||
                              event.key === " "
                            ) {
                              event.preventDefault();
                              if (rowOpensWorkspace || rowOpensInline) {
                                openRequest(request);
                              } else {
                                openRequestDetail(request.id);
                              }
                            }
                          }
                          : undefined
                      }
                    >
                      <td>
                        {user.role === "PLANLAMA" ? (
                          <div className="planning-request-identity">
                            <div className="request-row-eyebrow">
                              <span>{request.id}</span>
                              <span
                                className={`request-row-priority is-${request.priority === "Kritik"
                                    ? "critical"
                                    : request.priority === "Yüksek"
                                      ? "high"
                                      : "normal"
                                  }`}
                              >
                                {request.priority} öncelik
                              </span>
                            </div>
                            <strong className="request-row-title">
                              {request.title}
                            </strong>
                          </div>
                        ) : (
                          <>
                            <strong className="request-row-title">
                              <span>{request.title}</span>
                            </strong>
                            {user.role === "SATIN_ALMA" ? (
                              <small className="request-row-product-meta">
                                <span>{request.id}</span>
                                <i aria-hidden="true" />
                                <span>
                                  {request.category ??
                                    "Kategori belirtilmedi"}
                                </span>
                              </small>
                            ) : (
                              <>
                                <small>
                                  {request.id} · {request.meta}
                                </small>
                                <small className="request-row-context">
                                  {request.department} · {request.requester}
                                </small>
                              </>
                            )}
                          </>
                        )}
                        {user.role === "SATIN_ALMA" &&
                          request.status === "Düzeltme istendi" &&
                          returnContext?.decisionNote ? (
                          <span className="request-return-brief">
                            <span>Müdür geri bildirimi</span>
                            <strong title={returnContext.decisionNote}>
                              {returnContext.decisionNote}
                            </strong>
                            {returnContext.revisionFields?.length ? (
                              <small>
                                {returnContext.revisionFields.length} alan
                                güncellenecek
                              </small>
                            ) : null}
                          </span>
                        ) : null}
                      </td>
                      {user.role === "SATIN_ALMA" ? (
                        <td className="request-quantity-cell">
                          <strong>
                            {request.quantity !== undefined
                              ? request.quantity.toLocaleString("tr-TR")
                              : "—"}{" "}
                            <span>
                              {request.unit ?? "Birim belirtilmedi"}
                            </span>
                          </strong>
                        </td>
                      ) : null}
                      {user.role === "SATIN_ALMA" ? (
                        <td className="request-owner-cell">
                          <strong>{request.requester}</strong>
                          <small>{request.department}</small>
                        </td>
                      ) : null}
                      {user.role === "PLANLAMA" ? (
                        <td className="planning-request-facts">
                          <strong>{request.meta}</strong>
                          <span>{formatDisplayDate(request.due)}</span>
                        </td>
                      ) : null}
                      {user.role === "PLANLAMA" ? (
                        <td className="planning-request-requester">
                          <strong>{request.requester}</strong>
                        </td>
                      ) : null}
                      <td className="request-process-cell">
                        {user.role === "SATIN_ALMA" ? (
                          <>
                            <strong>
                              {request.offers === 0
                                ? "İlk teklifi ekle"
                                : request.offers === 1
                                  ? "Alternatif teklif ekle"
                                  : (nextStep?.action ?? "Teklifleri incele")}
                            </strong>
                            <small>
                              {request.offers === 0
                                ? "Tedarikçi ve fiyat bilgisini girin"
                                : request.offers === 1
                                  ? "1 teklif kayıtlı"
                                  : `${request.offers} teklif karşılaştırılabilir`}
                            </small>
                          </>
                        ) : user.role === "PLANLAMA" ? (
                          <PlanningRequestProgress request={request} />
                        ) : null}
                        {user.role !== "SATIN_ALMA" &&
                          user.role !== "PLANLAMA" ? (
                          <div className="request-urgency-meta">
                            <strong>{stage}</strong>
                            <small>{responsible}</small>
                          </div>
                        ) : null}
                      </td>
                      {can(user, "offer.read") &&
                        user.role !== "SATIN_ALMA" ? (
                        <td className="request-offer-cell">
                          <strong>{request.offers || 0} teklif</strong>
                          <small>
                            {request.offers > 1
                              ? "Karşılaştırmaya hazır"
                              : request.offers === 1
                                ? "Tek kaynak mevcut"
                                : "Henüz başlanmadı"}
                          </small>
                        </td>
                      ) : null}
                      {user.role !== "PLANLAMA" ? (
                        <td className="request-due-cell">
                          <strong>{formatDisplayDate(request.due)}</strong>
                          {user.role === "SATIN_ALMA" ? (
                            <>
                              <small
                                className={`request-due-status${formatRemainingDays(request.due)?.includes("geçti") ? " is-overdue" : ""}`}
                              >
                                {formatRemainingDays(request.due)}
                              </small>
                              {request.priority !== "Normal" ? (
                                <em
                                  className={`request-row-priority-signal request-due-priority is-${request.priority === "Kritik" ? "critical" : "high"}`}
                                >
                                  {request.priority} öncelik
                                </em>
                              ) : null}
                            </>
                          ) : null}
                        </td>
                      ) : null}
                      {user.role === "SATIN_ALMA" ? (
                        <td>
                          <ProcurementQueueOpenIndicator />
                        </td>
                      ) : user.role === "PLANLAMA" ? (
                        <td className="request-row-toggle-cell">
                          <button
                            className="request-row-toggle"
                            aria-label={
                              expandedRequest === request.id
                                ? `${request.title} sürecini kapat`
                                : `${request.title} sürecini göster`
                            }
                            aria-expanded={expandedRequest === request.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              openRequest(request);
                            }}
                          >
                            {expandedRequest === request.id ? (
                              <ChevronDown size={17} />
                            ) : (
                              <ChevronRight size={17} />
                            )}
                          </button>
                        </td>
                      ) : (
                        <td>
                          <button
                            className="request-row-action"
                            aria-label={
                              opensInline
                                ? expandedRequest === request.id
                                  ? `${request.title} sürecini kapat`
                                  : `${request.title} sürecini göster`
                                : isCompleted
                                  ? `${request.title} sonucunu görüntüle`
                                  : `${request.title} talebini aç`
                            }
                            title={
                              opensInline
                                ? expandedRequest === request.id
                                  ? "Süreci kapat"
                                  : "Süreci göster"
                                : isCompleted
                                  ? "Sonucu görüntüle"
                                  : "Talebi aç"
                            }
                            aria-expanded={
                              opensInline
                                ? expandedRequest === request.id
                                : undefined
                            }
                            onClick={(event) => {
                              event.stopPropagation();
                              if (isCompleted && rowIsInteractive) {
                                openRequestDetail(request.id);
                                return;
                              }
                              openRequest(request);
                            }}
                          >
                            <span>{actionLabel}</span>
                            {opensInline && expandedRequest === request.id ? (
                              <ChevronDown size={15} />
                            ) : (
                              <ChevronRight size={15} />
                            )}
                          </button>
                        </td>
                      )}
                    </tr>
                    {expandedRequest === request.id ? (
                      <tr className="request-inline-detail-row">
                        <td
                          colSpan={
                            user.role === "SATIN_ALMA"
                              ? 6
                              : user.role === "PLANLAMA"
                                ? 5
                                : 4 + (can(user, "offer.read") ? 1 : 0)
                          }
                        >
                          <div className="request-inline-workspace">
                            {user.role === "PLANLAMA" ? (
                              <PlanningRequestInline
                                requestNo={request.id}
                                service={requestWorkspaceService}
                              />
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <TableState
          title="Eşleşen talep bulunamadı"
          description="Arama ifadesini değiştirin veya filtreleri temizleyin."
        />
      )}
    </section>
  </>);
}
